'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'

import { cn } from '@/utilities/ui'

import { HomeHeaderFilterBar } from './HomeHeaderFilterBar'
import { HomeMiddleDestaque } from './HomeMiddleDestaque'
import { HomeFooterDestaque } from './HomeFooterDestaque'
import { HomeNewsCarouselBand } from './HomeNewsCarouselBand'
import { HomeXPostsBand } from './HomeXPostsBand'
import type { SectionHeadingLevel } from './common'
import type {
  DestaquePublicacao,
  FooterNewsItem,
  HomeFeedQuery,
  HomeFeedShape,
  HomeFooterLoadMoreConfig,
  HomeHeaderFilterButton,
  HomeXPostCardVM,
} from './types'

type BandHeader = {
  sectionHeading?: string
  sectionHeadingLevel?: SectionHeadingLevel
  showSectionHeader?: boolean
  showMoreLink?: boolean
  moreTopicLabel?: string
  moreHref?: string
  headingId?: string
  /** Só faixa X (xcard): rótulo do link de cada card. */
  cardLinkLabel?: string
}

type BandItem = DestaquePublicacao | FooterNewsItem | HomeXPostCardVM

type Props = {
  shape: HomeFeedShape
  buttons: HomeHeaderFilterButton[]
  baseQuery: HomeFeedQuery
  limit: number
  initialItems: BandItem[]
  header: BandHeader
  showThumbnail?: boolean
  priorityImageLoad?: boolean
  /** Footer: config de "Carregar mais" — só ativa no estado Default. */
  footerLoadMore?: HomeFooterLoadMoreConfig
}

const ENDPOINT = '/home-feed/band'
const DEFAULT_ID = '__default__'

/**
 * Envolve a lista de uma faixa editorial e troca os posts conforme o botão do Header Filter.
 * Genérico por `shape` (destaque | footer | xcard) — serve Middle, Footer e X mantendo o
 * componente de apresentação de cada faixa.
 */
export function HomeBandFilterable({
  shape,
  buttons,
  baseQuery,
  limit,
  initialItems,
  header,
  showThumbnail = true,
  priorityImageLoad = true,
  footerLoadMore,
}: Props) {
  const t = useTranslations('Home')
  const locale = useLocale()
  const [activeId, setActiveId] = useState(DEFAULT_ID)
  const [items, setItems] = useState<BandItem[]>(initialItems)
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)
  const isDefault = activeId === DEFAULT_ID

  // "Ver mais" DINÂMICO:
  // - Pílula (navItems): usa `button.moreHref` — string = destino; `null` = ocultar o botão.
  // - Header Filter: usa `button.href` (arquivo da categoria/tag); no estado padrão, o `moreHref` do bloco.
  const activeButton = buttons.find((b) => b.id === activeId)
  const resolvedMoreHref =
    activeButton?.moreHref !== undefined
      ? activeButton.moreHref
      : (activeButton?.href ?? header.moreHref)

  const onSelect = async (button: HomeHeaderFilterButton) => {
    if (loading || button.id === activeId) return
    setActiveId(button.id)
    setErrored(false)
    if (!button.override) {
      setItems(initialItems)
      return
    }
    setLoading(true)
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ shape, limit, query: baseQuery, override: button.override, locale }),
      })
      if (!res.ok) throw new Error('bad response')
      const data = (await res.json()) as { items?: BandItem[] }
      setItems(data.items ?? [])
    } catch {
      setErrored(true)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  const bar = (
    <HomeHeaderFilterBar
      buttons={buttons}
      activeId={activeId}
      onSelect={onSelect}
      loading={loading}
      className="mt-3 lg:mt-4 lg:pb-4"
    />
  )

  function renderContent() {
    if (items.length === 0) {
      if (isDefault) return null
      const aviso = (
        <p className="text-muted-foreground py-10 text-sm" role="status">
          {errored ? t('filterLoadError') : t('filterNoPosts')}
        </p>
      )
      // A faixa «carrossel» embute a barra no próprio bloco (`filterSlot`), e o retorno abaixo
      // não passa por lá. Sem repetir a barra aqui, uma pílula sem resultado deixaria o leitor
      // preso no aviso, sem nenhum controlo para voltar.
      if (shape !== 'newsCarousel') return aviso
      return (
        <>
          <div className="mb-4">{bar}</div>
          {aviso}
        </>
      )
    }

    if (shape === 'footer') {
      return (
        <HomeFooterDestaque
          items={items as FooterNewsItem[]}
          sectionHeading={header.sectionHeading}
          sectionHeadingLevel={header.sectionHeadingLevel}
          showSectionHeader={header.showSectionHeader}
          showMoreLink={header.showMoreLink}
          moreTopicLabel={header.moreTopicLabel}
          moreHref={resolvedMoreHref ?? undefined}
          showThumbnail={showThumbnail}
          loadMore={isDefault ? footerLoadMore : undefined}
        />
      )
    }

    if (shape === 'newsCarousel') {
      return (
        <HomeNewsCarouselBand
          items={items as DestaquePublicacao[]}
          sectionHeading={header.sectionHeading}
          sectionHeadingLevel={header.sectionHeadingLevel}
          showSectionHeader={header.showSectionHeader}
          showMoreLink={header.showMoreLink !== false && resolvedMoreHref != null}
          moreTopicLabel={header.moreTopicLabel}
          moreHref={resolvedMoreHref ?? undefined}
          headingId={header.headingId}
          showThumbnail={showThumbnail}
          filterSlot={bar}
        />
      )
    }

    if (shape === 'xcard') {
      return (
        <HomeXPostsBand
          posts={items as HomeXPostCardVM[]}
          sectionHeading={header.sectionHeading}
          sectionHeadingLevel={header.sectionHeadingLevel}
          showSectionHeader={header.showSectionHeader}
          showMoreLink={header.showMoreLink}
          moreTopicLabel={header.moreTopicLabel}
          moreHref={resolvedMoreHref ?? undefined}
          headingId={header.headingId}
          showThumbnail={showThumbnail}
          cardLinkLabel={header.cardLinkLabel}
        />
      )
    }

    return (
      <HomeMiddleDestaque
        items={items as DestaquePublicacao[]}
        sectionHeading={header.sectionHeading}
        sectionHeadingLevel={header.sectionHeadingLevel}
        showSectionHeader={header.showSectionHeader}
        // Oculta "Ver mais" quando a pílula activa não define destino (resolvedMoreHref === null).
        showMoreLink={header.showMoreLink !== false && resolvedMoreHref != null}
        moreTopicLabel={header.moreTopicLabel}
        moreHref={resolvedMoreHref ?? undefined}
        priorityImageLoad={priorityImageLoad}
        showThumbnail={showThumbnail}
        navItems={[]}
        filterSlot={bar}
      />
    )
  }

  // Na faixa destaque (home-meio) a barra de filtro entra DENTRO do bloco de cabeçalho
  // (via `filterSlot`), preservando o layout h2 + "Ver mais" + chips. Nas demais faixas
  // (footer/xcard) a barra fica acima da lista.
  return (
    <div data-home-region="band-filterable">
      {shape !== 'destaque' && shape !== 'newsCarousel' ? bar : null}
      <div
        aria-busy={loading || undefined}
        className={cn(
          'transition-opacity',
          shape !== 'destaque' && shape !== 'newsCarousel' && 'mt-3',
          loading && 'opacity-60',
        )}
      >
        {renderContent()}
      </div>
    </div>
  )
}
